import { computed, inject } from '@angular/core'
// Feature services, models
import { INITIAL_STATE } from './hub-auth.state'
import type { ParsedToken } from '../models'
import { StorageEnum, StorageKeyEnum } from '../enums'
// HUB modules
import { StorageService } from '@inside-hub-app/hub-storage'
// 3rd party modules
import { NGXLogger } from 'ngx-logger'
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals'
import { toObservable } from '@angular/core/rxjs-interop'
import { lastValueFrom } from 'rxjs'
import { jwtDecode } from 'jwt-decode'
import { HubConfigurationService } from '@inside-hub-app/hub-configuration'
import Keycloak from 'keycloak-js'

export const HubAuthStore = signalStore(
  { providedIn: 'root' },
  withState(INITIAL_STATE),
  withProps(({ _token }) => ({
    _storageService: inject(StorageService),
    _hubConfigurationService: inject(HubConfigurationService),
    _logger: inject(NGXLogger),
    _keycloak: inject(Keycloak),
    observeTokenChange$: toObservable(_token)
  })),
  withComputed(({ _token, _hubConfigurationService }) => ({
    token: computed(() => { return _token() ?? null }),
    isKeycloakEnabled: computed(() => _hubConfigurationService.get<boolean>('hub.auth.keycloak.enabled'))
  })),
  // private inner methods
  withMethods(({ _storageService, _keycloak }) => ({
    _getToken: async (): Promise<string | null> => {
      // Read the current token directly (no refresh); used for inspection only.
      let token: string | null | undefined = _keycloak?.token
      if (!token) {
        token = await lastValueFrom(
          _storageService.get<string>(StorageEnum.PREFIX + StorageKeyEnum.TOKEN)
        )
      }
      return token ?? null
    },
    _isCdbToken (token: string): boolean {
      return token.split('.').length < 3
    }
  })),
  // Methods to interact with the store
  withMethods(({ _storageService, _logger, _getToken, ...store }) => ({
    rehydrateAuthStoreState: async () => {
      try {
        const token = await _getToken()
        patchState(store, { _token: token ?? null })
      } catch (err) {
        _logger.error('[HubAuthStore] Initialization failed:', err)
        patchState(store, { _token: null })
      }
    },
    setToken: async (token: string) => {
      try {
        await lastValueFrom(_storageService.set(StorageEnum.PREFIX + StorageKeyEnum.TOKEN, token))
        patchState(store, { _token: token })
        return token
      } catch (err) {
        patchState(store, { _token: null })
        _logger.error('[HubAuthStore] setting token failed:', err)
        return null
      }
    },
    /**
     * Updates the in-memory token without writing it to storage.
     * Use this for Keycloak-based flows where the token lives in the Keycloak
     * instance itself; we only mirror it into the store signal for components.
     */
    setTokenInMemory: (token: string | null): void => {
      patchState(store, { _token: token ?? null })
    },
    setExitImpersonateReturnPartnerId: async (partnerId: number) => {
      try {
        await lastValueFrom(_storageService.set(StorageEnum.PREFIX + StorageKeyEnum.EXIT_IMPERSONATE_RETURN_PARTNER_ID, partnerId))
      } catch (err) {
        _logger.error('[HubAuthStore] Setting exit impersonate return partner id failed:', err)
      }
    },
    clearStoreAndStorageData: async () => {
      try {
        await lastValueFrom(_storageService.clear())
        patchState(store, { _token: null })
      } catch (err) {
        _logger.error('[HubAuthStore] Clearing storage failed:', err)
        patchState(store, { _token: null })
      }
    }
  })),
  // Getter methods
  withMethods(({ _logger, _storageService, _getToken, _isCdbToken }) => {
    async function isImpersonateMode (): Promise<boolean> {
      try {
        const token = await _getToken()

        if (!token || _isCdbToken(token)) {
          return false
        }

        const decoded: ParsedToken = jwtDecode(token)

        return decoded?.impersonation ?? false
      } catch (err) {
        _logger.info('[HubAuthStore] Checking is impersonate mode failed:', err)
        return false
      }
    }

    async function isSameUserAsToken (userId: number): Promise<boolean> {
      const token = await _getToken()
      const decoded: ParsedToken = jwtDecode(token)

      return decoded.user_id === userId
    }

    /**
     * Reads the pre-impersonate partner ID from storage and deletes it afterwards.
     * This is a "read-once" function: the value will no longer exist in storage after reading.
     * @returns {Promise<number | null>} The ID of a partner on which was admin before impersonation, or null if not set or if still in impersonate mode.
    */
    async function getExitImpersonateReturnPartnerId (): Promise<number | null> {
      try {
        const isImpersonateModeActive = await isImpersonateMode()
        if (isImpersonateModeActive) return null

        const storageKey = StorageEnum.PREFIX + StorageKeyEnum.EXIT_IMPERSONATE_RETURN_PARTNER_ID
        const partnerId = await lastValueFrom(_storageService.get<number>(storageKey))

        if (partnerId != null) {
          await lastValueFrom(_storageService.delete(storageKey))
        }

        return partnerId ?? null
      } catch (err) {
        this.logger.error('[HubAuthStore] Getting exit impersonate return partner id failed:', err)
        return null
      }
    }

    return {
      isCdbToken: _isCdbToken,
      isImpersonateMode,
      isSameUserAsToken,
      getExitImpersonateReturnPartnerId
    }
  })
)
